import { useLayoutEffect, useMemo, useState } from "react"
import Script from "next/script"

import type { Nullish } from "~/@types/generics"

declare global {
  interface Window {
    JOIN_ENV: Record<string, { disabled?: boolean }> | null
    joinStories: {
      widgets: Record<
        string,
        {
          load: () => void
          loaded: boolean
          unload: () => void
        }
      >
    }
  }
}

export function useJoinStories({ handle }: { handle: Nullish<string> }) {
  const [joinStoriesSpecifiedProductAlias, setJoinStoriesSpecifiedProductAlias] = useState<Record<string, boolean>>({})

  const joinWidgetPreAlias = "join-widget"
  const currentAlias = handle
  const defaultAlias = `parure-percale-de-coton-120-fils`

  const isSpecifiedProductAlias = useMemo(
    () => (currentAlias ? currentAlias in joinStoriesSpecifiedProductAlias : false),
    [currentAlias, joinStoriesSpecifiedProductAlias]
  )

  const loadDefaultStory = currentAlias ? joinStoriesSpecifiedProductAlias?.[currentAlias] === false : false
  const loadSpecificStory = !isSpecifiedProductAlias

  useLayoutEffect(() => {
    if (window?.joinStories) {
      Object.keys(window?.joinStories?.widgets)?.map((el) => {
        const widget = window?.joinStories?.widgets[el]
        widget?.load()
      })
    }

    return () => {
      if (window?.joinStories) {
        Object.keys(window?.joinStories?.widgets)?.map((el) => {
          const widget = window?.joinStories?.widgets[el]
          if (el == defaultAlias || el == currentAlias) {
            widget?.load()
          } else {
            widget?.unload()
          }
        })
      }
    }
  }, [])

  const JoinStoriesScript = () => {
    if (loadDefaultStory) {
      return (
        <Script
          id={defaultAlias as string}
          src={`https://bonsoirs.my.join-stories.com/widgets/${defaultAlias}/index.js`}
          key={`${joinWidgetPreAlias}-${defaultAlias}`}
          data-join-widget-id={`${joinWidgetPreAlias}-${defaultAlias}`}
          data-join-widget-alias={defaultAlias}
          type="text/javascript"
          strategy="lazyOnload"
        />
      )
    }

    if (isSpecifiedProductAlias) return null

    if (loadSpecificStory) {
      return (
        <Script
          id={currentAlias ?? ""}
          src={`https://bonsoirs.my.join-stories.com/widgets/${currentAlias}/index.js`}
          key={`${joinWidgetPreAlias}-${currentAlias}`}
          data-join-widget-id={`${joinWidgetPreAlias}-${currentAlias}`}
          data-join-widget-alias={currentAlias}
          type="text/javascript"
          strategy="lazyOnload"
          onError={() => {
            setJoinStoriesSpecifiedProductAlias((prev) => ({ ...prev, [currentAlias as string]: false }))
          }}
          onReady={() => {
            setJoinStoriesSpecifiedProductAlias((prev) => ({ ...prev, [currentAlias as string]: true }))
          }}
        />
      )
    }
  }

  return {
    JoinStoriesScript,
    defaultAlias: defaultAlias,
    productAlias: handle,
    joinWidgetPreAlias,
  }
}
